// client/src/components/dashboard/properties/EditPropertyForm.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSave, FaTrashAlt } from 'react-icons/fa'; // Icons for buttons
import countriesList from '../../../utils/countries'; 
import { updateProperty, deleteProperty } from '../../../api/authApi'; 
import LoadingSpinner from '../../common/LoadingSpinner';

import './AddPropertyForm.css';

// --- Define Category -> Type Relationship (Copied from Add Form - Consider moving to utils) ---
const propertyHierarchy = {
    residential: [
        { value: 'house', labelKey: 'properties.type.house', defaultLabel: 'House' },
        { value: 'apartment', labelKey: 'properties.type.apartment', defaultLabel: 'Apartment' },
        { value: 'room', labelKey: 'properties.type.room', defaultLabel: 'Room' },
        { value: 'other_residential', labelKey: 'properties.type.other_residential', defaultLabel: 'Other Residential' }
    ],
    agricultural: [{ value: 'farm', labelKey: 'properties.type.farm', defaultLabel: 'Farm' }, /* ... */],
    commercial: [{ value: 'office', labelKey: 'properties.type.office', defaultLabel: 'Office' }, /* ... */],
    industrial: [{ value: 'factory', labelKey: 'properties.type.factory', defaultLabel: 'Factory' }, /* ... */],
    outdoor: [{ value: 'garden', labelKey: 'properties.type.garden', defaultLabel: 'Garden' }, /* ... */],
    other: [{ value: 'other', labelKey: 'properties.type.other', defaultLabel: 'Other' }]
};
const categoryOptions = [
    { value: 'residential', labelKey: 'properties.category.residential', defaultLabel: 'Residential' },
    { value: 'agricultural', labelKey: 'properties.category.agricultural', defaultLabel: 'Agricultural' },
    { value: 'commercial', labelKey: 'properties.category.commercial', defaultLabel: 'Commercial' },
    { value: 'industrial', labelKey: 'properties.category.industrial', defaultLabel: 'Industrial' },
    { value: 'outdoor', labelKey: 'properties.category.outdoor', defaultLabel: 'Outdoor/Utility' },
    { value: 'other', labelKey: 'properties.category.other', defaultLabel: 'Other' },
];
// --- End Data Structures ---

// Helper function to capitalize words
const capitalizeName = (name) => {
    if (!name) return '';
    return name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

function EditPropertyForm({ propertyData, onUpdateSuccess, onDeleteSuccess, onCancel }) {
    const { t } = useTranslation();

    // Initialize state from the propertyData prop
    const [propertyName, setPropertyName] = useState('');
    const [propertyCategory, setPropertyCategory] = useState('');
    const [propertyType, setPropertyType] = useState('');
    const [filteredTypeOptions, setFilteredTypeOptions] = useState([]);
    const [addressStreet, setAddressStreet] = useState('');
    const [addressCity, setAddressCity] = useState('');
    const [addressState, setAddressState] = useState('');
    const [addressPostalCode, setAddressPostalCode] = useState('');
    const [addressCountry, setAddressCountry] = useState('');

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false); // Loading state for Update/Delete actions

    // Effect to populate form when propertyData prop is received/changed
    useEffect(() => {
        if (propertyData) {
            setPropertyName(propertyData.property_name || '');
            setPropertyCategory(propertyData.property_category || '');
            setPropertyType(propertyData.property_type || '');
            setAddressStreet(propertyData.address_street || '');
            setAddressCity(propertyData.address_city || '');
            setAddressState(propertyData.address_state || '');
            setAddressPostalCode(propertyData.address_postal_code || '');
            setAddressCountry(propertyData.address_country || '');

            // Set initial filtered types based on the received category
            setFilteredTypeOptions(propertyHierarchy[propertyData.property_category] || []);
            setError(''); // Clear errors when new data is loaded
        } else {
            // Reset form if propertyData becomes null (e.g., on cancel maybe?)
            setPropertyName(''); setPropertyCategory(''); setPropertyType(''); setFilteredTypeOptions([]);
            setAddressStreet(''); setAddressCity(''); setAddressState(''); setAddressPostalCode(''); setAddressCountry('');
            setError('');
        }
    }, [propertyData]); // Re-run ONLY when propertyData changes


    // Handle Category Change - Updates filtered types and resets type
    const handleCategoryChange = (event) => {
        const newCategory = event.target.value;
        setPropertyCategory(newCategory);
        setPropertyType(''); // Reset type when category changes
        setFilteredTypeOptions(propertyHierarchy[newCategory] || []);
        setError('');
    };

    // Generic handler for other inputs
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setError(''); // Clear error on any input change

        switch (name) {
            case 'propertyName': setPropertyName(value); break;
            case 'propertyType': setPropertyType(value); break;
            case 'addressStreet': setAddressStreet(value); break;
            case 'addressCity': setAddressCity(value); break;
            case 'addressState': setAddressState(value); break;
            case 'addressPostalCode': setAddressPostalCode(value); break;
            case 'addressCountry': setAddressCountry(value); break;
            default: break;
        }
    };

    // --- Handle UPDATE Submit ---
    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!propertyName) {
            setError(t('properties.error.nameRequired', 'Property Name is required.')); return;
        }
        if (propertyName.length > 12) { // Length check
            setError(t('properties.error.nameTooLong', 'Property Name cannot exceed 12 characters.')); return;
        }

        const formattedName = capitalizeName(propertyName);
        setLoading(true);
        try {
            const payload = {
                // Include only fields that can be updated
                property_name: formattedName,
                property_type: propertyType || null,
                property_category: propertyCategory || null,
                address_street: addressStreet.trim() || null,
                address_city: addressCity.trim() || null,
                address_state: addressState.trim() || null,
                address_postal_code: addressPostalCode.trim() || null,
                address_country: addressCountry || null,
            };
            console.log(`Attempting to update property ID ${propertyData.property_id} with payload:`, payload);
            // Use mock/real API - needs property ID and payload
            await updateProperty(propertyData.property_id, payload);
            onUpdateSuccess(); // Call parent callback to hide form and refresh list
        } catch (err) {
            console.error("Error updating property:", err);
            setError(err.message || t('errors.api.requestFailed'));
        } finally {
            setLoading(false);
        }
    };

    // --- Handle DELETE Click ---
    const handleDeleteClick = async () => {
        setError('');
        // Add translation key for confirmation
        // if (!window.confirm(t('properties.confirmDelete', `Are you sure you want to delete property "${propertyName || 'this property'}"? This action cannot be undone.`))) {
        //     return;
        // }
        setLoading(true);
        try {
            console.log(`Attempting to delete property ID ${propertyData.property_id}`);
            // Use mock/real API - needs property ID
            await deleteProperty(propertyData.property_id);
            onDeleteSuccess(); // Call parent callback to hide form and refresh list
        } catch (err) {
            console.error("Error deleting property:", err);
            setError(err.message || t('errors.api.requestFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Render the form
    return (
        <div className="edit-property-form add-property-form"> {/* Reuse add-form styles */}
            {/* Add property name to title */}
            <h2>{t('properties.editFormTitle', 'Edit Property')}: {propertyData?.property_name || ''}</h2>
            {error && <div className="form-error">{error}</div>}
            {/* Use handleUpdateSubmit for the form's onSubmit */}
            <form onSubmit={handleUpdateSubmit}>
                <div className="form-row-3-column">
                    {/* Property Name (Required) */}
                    <div className="form-group">
                        <label htmlFor="editPropertyName">{t('properties.nameLabel', 'Property Name')}:*</label>
                        <input type="text" id="editPropertyName" name="propertyName" value={propertyName} onChange={handleInputChange} required maxLength={12} />
                    </div>

                    {/* Property Category Select */}
                    <div className="form-group">
                        <label htmlFor="editPropertyCategory">{t('properties.categoryLabel', 'Property Category')}:</label>
                        <select id="editPropertyCategory" name="propertyCategory" value={propertyCategory} onChange={handleCategoryChange}>
                            <option value="">{t('properties.selectCategory', 'Select Category (Optional)')}</option>
                            {categoryOptions.map(opt => (<option key={opt.value} value={opt.value}>{t(opt.labelKey, opt.defaultLabel)}</option>))}
                        </select>
                    </div>

                    {/* Property Type Select (Dependent) */}
                    <div className="form-group">
                        <label htmlFor="editPropertyType">{t('properties.typeLabel', 'Property Type')}:</label>
                        <select id="editPropertyType" name="propertyType" value={propertyType} onChange={handleInputChange} disabled={!propertyCategory || filteredTypeOptions.length === 0} >
                            <option value="">{t('properties.selectType', 'Select Type (Optional)')}</option>
                            {filteredTypeOptions.map(opt => (<option key={opt.value} value={opt.value}>{t(opt.labelKey, opt.defaultLabel)}</option>))}
                        </select>
                    </div>
                </div>
                {/* Optional Address Fields */}
                <div className="form-group">
                    <label htmlFor="editAddressStreet">{t('properties.addressStreetLabel', 'Street Address (Optional)')}:</label>
                    <input type="text" id="editAddressStreet" name="addressStreet" value={addressStreet} onChange={handleInputChange} />
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="editAddressCity">{t('properties.addressCityLabel', 'City (Optional)')}:</label>
                        <input type="text" id="editAddressCity" name="addressCity" value={addressCity} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="editAddressState">{t('properties.addressStateLabel', 'State/Province (Optional)')}:</label>
                        <input type="text" id="editAddressState" name="addressState" value={addressState} onChange={handleInputChange} />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="editAddressPostalCode">{t('properties.addressPostalCodeLabel', 'Postal Code (Optional)')}:</label>
                        <input type="text" id="editAddressPostalCode" name="addressPostalCode" value={addressPostalCode} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="editAddressCountry">{t('properties.addressCountryLabel', 'Country (Optional)')}:</label>
                        <select id="editAddressCountry" name="addressCountry" value={addressCountry} onChange={handleInputChange} >
                            <option value="">{t('dashboard.account.contact.selectCountry')}</option>
                            {countriesList.map(country => (<option key={country.value} value={country.value}>{t(`countries.${country.value}`, country.label)}</option>))}
                        </select>
                    </div>
                </div>

                {/* Action Buttons: Update, Cancel, Delete */}
                <div className="form-actions edit-form-actions">
                    {/* Update Button is the submit button */}
                    <button type="submit" className="form-action-button save-button" disabled={loading}>
                        {loading ? t('common.saving', 'Saving...') : <><FaSave /> {t('common.update', 'Update Property')}</>}
                    </button>
                    {/* Cancel Button */}
                    <button type="button" className="form-action-button cancel-button" onClick={onCancel} disabled={loading}>
                        {t('common.cancel', 'Cancel')}
                    </button>
                    {/* Delete Button */}
                    <button type="button" className="form-action-button delete-button" onClick={handleDeleteClick} disabled={loading} style={{ marginLeft: 'auto', backgroundColor: '#dc3545' }}>
                        {/* Show spinner specifically for delete if needed */}
                        {loading ? <LoadingSpinner/>: <FaTrashAlt />} {t('common.delete', 'Delete')}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default EditPropertyForm;